<template>
  <div class="rd-screen wh-docs" :class="{ 'wm-docs rm-screen': mobile }">
    <!-- ============================================================ računar -->
    <template v-if="!mobile">
      <report-tools :copy="shownRows.length > 0" @copy="copyTable">
        <ui-segment v-if="aggregateAvailable" :value="aggregate" :options="viewOptions" :label="$t('view')" @input="$emit('update:aggregate', $event)">
          <template #option="{ option }">
            <v-icon v-if="option.icon" size="16">{{ option.icon }}</v-icon>
            {{ option.label }}
          </template>
        </ui-segment>
        <ui-chips v-if="chips.length" :value="chip" :options="chips" :label="$t('status')" @input="$emit('update:chip', $event)" />
        <button v-if="hint && hintClickable" type="button" class="ui-text-btn wh-hint" data-hint @click="$emit('hint')">
          <v-icon size="16">mdi-history</v-icon>
          {{ hint }}
        </button>
        <span v-else-if="hint" class="wh-hint rd-muted" data-hint>
          <v-icon size="16">mdi-history</v-icon>
          {{ hint }}
        </span>

        <template #right>
          <template v-for="action in actions">
            <v-menu v-if="action.menu" :key="action.key" offset-y left content-class="rd-vmenu">
              <template #activator="{ on, attrs }">
                <button type="button" class="ui-btn ui-btn--sm" :class="toneClass(action.tone)" :data-action="action.key" v-bind="attrs" v-on="on">
                  <v-icon size="16" :color="iconColor(action.tone)">{{ action.icon }}</v-icon>
                  {{ action.label }}
                  <v-icon size="16" :color="iconColor(action.tone)">mdi-chevron-down</v-icon>
                </button>
              </template>
              <ul class="rd-vmenu__list" role="menu">
                <li v-for="item in action.menu" :key="item.key">
                  <button type="button" role="menuitem" :data-action="item.key" @click="$emit('action', item.key)">
                    <v-icon size="16">{{ item.icon }}</v-icon>
                    {{ item.label }}
                  </button>
                </li>
              </ul>
            </v-menu>
            <button
              v-else
              :key="action.key"
              type="button"
              class="ui-btn ui-btn--sm"
              :class="toneClass(action.tone)"
              :data-action="action.key"
              :disabled="action.disabled"
              @click="$emit('action', action.key)"
            >
              <v-icon size="16" :color="iconColor(action.tone)">{{ action.icon }}</v-icon>
              {{ action.label }}
            </button>
          </template>
        </template>
      </report-tools>

      <report-state v-if="loading" kind="loading"></report-state>
      <report-state v-else-if="!shownRows.length" :icon="empty.icon" :title="empty.title" :text="empty.text">
        <button v-if="hint && hintClickable" type="button" class="ui-text-btn" @click="$emit('hint')">{{ hint }}</button>
      </report-state>

      <template v-else>
        <div ref="card" class="ui-card rd-card">
          <table class="ui-table rd-tbl wh-tbl wh-doc-tbl" :class="{ 'wh-doc-tbl--aggregate': aggregate }">
            <thead>
              <tr>
                <th v-if="aggregate && expandable" scope="col" class="wh-col-expand"></th>
                <th
                  v-for="column in activeColumns"
                  :key="column.key"
                  scope="col"
                  :class="columnClass(column)"
                  :aria-sort="column.sort === false ? null : ariaSort(column.key)"
                >
                  <button v-if="column.sort !== false" type="button" @click="sortBy(column.key)">
                    {{ column.label }} {{ sortArrow(column.key) }}
                  </button>
                  <template v-else>{{ column.label }}</template>
                </th>
                <th v-if="hasRowActions" scope="col" class="wh-col-actions" :aria-label="$t('actions')"></th>
              </tr>
            </thead>
            <tbody v-for="group in visibleGroups" :key="group.key">
              <tr v-if="group.name" class="rd-tbl__group">
                <td :colspan="colCount">
                  {{ group.name }}
                  <span class="rd-muted rd-num">· {{ group.total }}</span>
                </td>
              </tr>
              <template v-for="row in group.rows">
                <tr :key="row.key" :data-open="onRowOpen ? '' : null" :class="rowClass(row)" @click="openRow(row)">
                  <td v-if="aggregate && expandable" class="wh-col-expand" @click.stop>
                    <button
                      type="button"
                      class="wh-expand"
                      :class="{ 'is-open': expanded === row.key }"
                      :aria-expanded="String(expanded === row.key)"
                      :aria-label="$t('warehouse.document_count')"
                      @click="toggleExpand(row)"
                    >
                      <v-icon size="18">mdi-chevron-down</v-icon>
                    </button>
                  </td>
                  <td v-for="column in activeColumns" :key="column.key" :class="cellClass(column)">
                    <slot name="cell" :row="row" :column="column">{{ show(row[column.key]) }}</slot>
                  </td>
                  <td v-if="hasRowActions" class="wh-col-actions" @click.stop>
                    <span class="wh-row-actions"><slot name="row-actions" :row="row"></slot></span>
                  </td>
                </tr>
                <tr v-if="aggregate && expandable && expanded === row.key" :key="`${row.key}-detail`" class="wh-detail">
                  <td :colspan="colCount">
                    <table class="wh-inner">
                      <thead>
                        <tr>
                          <th v-for="column in detailColumns" :key="column.key" scope="col" :class="columnClass(column)">{{ column.label }}</th>
                          <th v-if="hasDetailActions" scope="col" class="wh-col-actions"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in detailRows(row)" :key="item.key">
                          <td v-for="column in detailColumns" :key="column.key" :class="cellClass(column)">
                            <slot name="detail-cell" :row="item" :column="column">{{ show(item[column.key]) }}</slot>
                          </td>
                          <td v-if="hasDetailActions" class="wh-col-actions">
                            <span class="wh-row-actions"><slot name="detail-actions" :row="item"></slot></span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div v-if="hiddenCount > 0" class="wh-more">
          <span class="rd-muted rd-num">{{ $t("warehouse_shell.shown_of", { shown: visibleCount, total: shownRows.length }) }}</span>
          <button type="button" class="ui-btn ui-btn--sm" data-action="more" @click="limit += pageSize">
            {{ $t("warehouse_shell.show_more") }}
          </button>
        </div>
      </template>
    </template>

    <!-- ============================================================ telefon -->
    <template v-else>
      <div v-if="aggregateAvailable" class="rm-segment" role="group" :aria-label="$t('view')">
        <button
          type="button"
          class="rm-segment__btn"
          :class="{ 'is-active': !aggregate }"
          data-view="documents"
          :aria-pressed="String(!aggregate)"
          @click="$emit('update:aggregate', false)"
        >
          {{ $t("warehouse_shell.documents") }}
        </button>
        <button
          type="button"
          class="rm-segment__btn"
          :class="{ 'is-active': aggregate }"
          data-view="aggregate"
          :aria-pressed="String(aggregate)"
          @click="$emit('update:aggregate', true)"
        >
          {{ $t("warehouse.aggregate") }}
        </button>
      </div>

      <div v-if="chips.length" class="rm-chip-row wm-status-row" role="group" :aria-label="$t('status')">
        <button
          v-for="item in chips"
          :key="String(item.value)"
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': chip === item.value }"
          :data-chip="String(item.value)"
          :aria-pressed="String(chip === item.value)"
          @click="$emit('update:chip', item.value)"
        >
          {{ item.label }}
          <b class="rm-num">{{ item.count }}</b>
        </button>
      </div>

      <button v-if="hint && hintClickable" type="button" class="wm-hint" data-hint @click="$emit('hint')">
        <v-icon size="16" color="#9a3412">mdi-history</v-icon>
        {{ hint }}
      </button>
      <p v-else-if="hint" class="wm-note" data-hint>
        <v-icon size="16" color="#5f5852">mdi-history</v-icon>
        {{ hint }}
      </p>

      <div v-if="loading" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="!shownRows.length" class="rm-empty">
        <v-icon size="40" color="#8a817a">{{ empty.icon }}</v-icon>
        <b>{{ empty.title }}</b>
        <span v-if="empty.text">{{ empty.text }}</span>
      </div>

      <template v-else>
        <section v-for="group in visibleGroups" :key="group.key" class="wm-group">
          <h2 v-if="group.name" class="rm-section-title">{{ group.name }} · {{ group.total }}</h2>
          <div class="rm-card">
            <ul class="rm-list">
              <li v-for="row in group.rows" :key="row.key" class="wm-doc" :class="rowClass(row)" :data-open="onRowOpen ? '' : null" @click="openRow(row)">
                <div class="wm-doc__head">
                  <span class="wm-doc__title">
                    <span v-if="titleColumn && !isEmpty(row[titleColumn.key])" class="wm-doc__name">
                      <slot name="cell" :row="row" :column="titleColumn">{{ show(row[titleColumn.key]) }}</slot>
                    </span>
                    <span v-if="subColumns.length" class="wm-doc__sub rm-num" :class="isEmpty(titleColumn && row[titleColumn.key]) ? 'wm-doc__name' : 'rm-muted'">
                      <template v-for="(column, index) in subColumns">
                        <span v-if="index">·</span>
                        <slot name="cell" :row="row" :column="column"><template v-if="column.prefix">{{ column.prefix }}</template>{{ show(row[column.key]) }}</slot>
                      </template>
                    </span>
                  </span>
                  <span v-if="badgeColumn || hasRowActions" class="wm-doc__side">
                    <span v-if="badgeColumn" class="wm-doc__badge">
                      <slot name="cell" :row="row" :column="badgeColumn">{{ show(row[badgeColumn.key]) }}</slot>
                    </span>
                    <span v-if="hasRowActions" class="wm-doc__actions" @click.stop>
                      <slot name="row-actions" :row="row"></slot>
                    </span>
                  </span>
                </div>
                <dl v-if="visibleMeta(row).length" class="wm-doc__meta">
                  <div v-for="column in visibleMeta(row)" :key="column.key">
                    <dt class="rm-muted">{{ column.label }}</dt>
                    <dd class="rm-num"><slot name="cell" :row="row" :column="column">{{ show(row[column.key]) }}</slot></dd>
                  </div>
                </dl>
                <div v-if="aggregate && expandable" class="wm-doc__expand" @click.stop>
                  <button type="button" class="rm-text-btn" :aria-expanded="String(expanded === row.key)" @click="toggleExpand(row)">
                    {{ $t("warehouse.document_count") }}: {{ row.document_count }}
                    <v-icon size="18" color="#c2410c">{{ expanded === row.key ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                  </button>
                  <ul v-if="expanded === row.key" class="rm-list wm-doc__details">
                    <li v-for="item in detailRows(row)" :key="item.key" class="wm-doc__detail">
                      <span class="wm-doc__detail-text">
                        <b class="rm-num">{{ show(item[detailTitleKey]) }}</b>
                        <span class="rm-muted rm-num">
                          <template v-for="(column, index) in detailMetaColumns"
                            ><template v-if="index"> · </template>{{ show(item[column.key]) }}</template>
                        </span>
                      </span>
                      <slot name="detail-actions" :row="item"></slot>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <div v-if="hiddenCount > 0" class="wm-more">
          <span class="rm-muted rm-num">{{ $t("warehouse_shell.shown_of", { shown: visibleCount, total: shownRows.length }) }}</span>
          <button type="button" class="rm-btn rm-btn--sm" data-action="more" @click="limit += pageSize">
            {{ $t("warehouse_shell.show_more") }}
          </button>
        </div>
      </template>

      <div v-if="actions.length" class="wm-bar">
        <button type="button" class="rm-btn rm-btn--primary" data-action="actions" @click="onBarAction">
          <v-icon size="20" color="#ffffff">{{ singleAction ? singleAction.icon : "mdi-plus-circle-outline" }}</v-icon>
          {{ singleAction ? singleAction.label : $t("actions") }}
        </button>
      </div>

      <ui-sheet v-model="actionsSheet" :label="$t('actions')">
        <ul class="rm-list rm-actions">
          <li v-for="item in sheetActions" :key="item.key">
            <button type="button" class="rm-actions__row" :data-action="item.key" @click="runSheetAction(item)">
              <v-icon size="22" :color="item.tone === 'danger' ? '#b42318' : '#1c1917'">{{ item.icon }}</v-icon>
              {{ item.label }}
            </button>
          </li>
        </ul>
      </ui-sheet>
    </template>
  </div>
</template>

<script>
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import "./warehouseDocs.scss";

const TONE_CLASS = { ok: "ui-btn--ok", pri: "ui-btn--primary", plain: "" };

const sortValue = (row, key) => {
  const raw = row.sort && key in row.sort ? row.sort[key] : row[key];
  return raw === undefined || raw === null ? "" : raw;
};

/**
 * Prikaz tabova magacina sa dokumentima (Dopuna, Izdavanje, Povrat, Otpis, Naručeno, Ponude, Popis, Transakcije,
 * Magacini): na računaru traka alata (Dokumenti / Zbirno, izbor stanja, poslednji unos, radnje) i tabela, na telefonu
 * kartice sa glavnom radnjom u donjoj traci. Ne zna ništa o dokumentima: dobija kolone i redove, javlja radnje.
 * Ekran koji ga koristi daje sadržaj ćelija i dugmad u redu kroz slotove (`cell`, `row-actions`, `detail-actions`).
 *
 * Kolona: { key, label, align?: "right"|"center", sort?: false, main?: true, mobile?: "title"|"sub"|"badge"|"meta"|"hide" }.
 * Red: { key, group?: { id, name }, ...vrednosti po kolonama, sort?: { kolona: vrednost za sortiranje } }.
 */
export default {
  name: "WarehouseDocScreen",
  components: { ReportState, ReportTools },
  props: {
    mobile: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    /** redovi su grupisani po magacinu (row.group) */
    grouped: { type: Boolean, default: false },
    aggregateAvailable: { type: Boolean, default: false },
    aggregate: { type: Boolean, default: false },
    aggregateColumns: { type: Array, default: () => [] },
    aggregateRows: { type: Array, default: () => [] },
    /** Zbirni red se otvara i pokazuje dokumente (detailRows(row)) */
    expandable: { type: Boolean, default: false },
    detailColumns: { type: Array, default: () => [] },
    detailRows: { type: Function, default: () => [] },
    hint: { type: String, default: "" },
    hintClickable: { type: Boolean, default: false },
    /** [{ key, label, icon, tone: "ok"|"pri"|"plain"|"danger", menu?: [{ key, label, icon }], disabled? }] */
    actions: { type: Array, default: () => [] },
    /** izbor stanja: [{ value, label, count, dot? }] i izabrana vrednost */
    chips: { type: Array, default: () => [] },
    chip: { type: [String, Number], default: null },
    empty: { type: Object, default: () => ({ icon: "mdi-file-document-outline", title: "", text: "" }) },
    pageSize: { type: Number, default: 100 },
    /** klik na red (ekran ga sluša kao @open-row) */
    clickable: { type: Boolean, default: false },
  },
  data() {
    return {
      sort: { key: null, dir: 1 },
      limit: this.pageSize,
      expanded: null,
      actionsSheet: false,
    };
  },
  computed: {
    viewOptions() {
      return [
        { value: false, label: this.$t("warehouse_shell.documents") },
        { value: true, label: this.$t("warehouse.aggregate"), icon: "mdi-sigma" },
      ];
    },
    onRowOpen() {
      return this.clickable;
    },
    /** Dugmad u redu važe za dokumente; zbirni red artikla ih nema (dokumenti su u otvorenom redu). */
    hasRowActions() {
      return Boolean(this.$scopedSlots["row-actions"]) && !this.aggregate;
    },
    hasDetailActions() {
      return Boolean(this.$scopedSlots["detail-actions"]);
    },
    activeColumns() {
      return this.aggregate ? this.aggregateColumns : this.columns;
    },
    colCount() {
      return this.activeColumns.length + (this.hasRowActions ? 1 : 0) + (this.aggregate && this.expandable ? 1 : 0);
    },
    shownRows() {
      const rows = this.aggregate ? this.aggregateRows : this.rows;
      let result = rows;
      if (this.sort.key) {
        const { key, dir } = this.sort;
        result = [...rows].sort((a, b) => {
          const first = sortValue(a, key);
          const second = sortValue(b, key);
          if (typeof first === "number" && typeof second === "number") {
            return (first - second) * dir;
          }
          return String(first).localeCompare(String(second), undefined, { numeric: true, sensitivity: "base" }) * dir;
        });
      }
      if (!this.grouped || this.aggregate) {
        return result;
      }
      // grupe ostaju zajedno, u redosledu prvog pojavljivanja (red unutar grupe ostaje kakav je)
      const order = [];
      result.forEach((row) => {
        if (!order.includes(row.group.id)) {
          order.push(row.group.id);
        }
      });
      return [...result].sort((a, b) => order.indexOf(a.group.id) - order.indexOf(b.group.id));
    },
    visibleRows() {
      return this.shownRows.slice(0, this.limit);
    },
    visibleCount() {
      return this.visibleRows.length;
    },
    hiddenCount() {
      return this.shownRows.length - this.visibleRows.length;
    },
    visibleGroups() {
      if (!this.grouped || this.aggregate) {
        return [{ key: "all", name: "", total: this.shownRows.length, rows: this.visibleRows }];
      }
      const totals = {};
      this.shownRows.forEach((row) => {
        totals[row.group.id] = (totals[row.group.id] || 0) + 1;
      });
      const groups = [];
      this.visibleRows.forEach((row) => {
        let group = groups[groups.length - 1];
        if (!group || group.key !== row.group.id) {
          group = { key: row.group.id, name: row.group.name, total: totals[row.group.id], rows: [] };
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups;
    },
    // ---- telefon: uloga kolona u kartici
    mobileColumns() {
      return this.activeColumns.filter((column) => column.mobile !== "hide");
    },
    titleColumn() {
      return this.mobileColumns.find((column) => column.mobile === "title") || this.mobileColumns[0] || null;
    },
    subColumns() {
      return this.mobileColumns.filter((column) => column.mobile === "sub");
    },
    badgeColumn() {
      return this.mobileColumns.find((column) => column.mobile === "badge") || null;
    },
    metaColumns() {
      return this.mobileColumns.filter(
        (column) => column !== this.titleColumn && column !== this.badgeColumn && column.mobile !== "sub"
      );
    },
    detailTitleKey() {
      return this.detailColumns.length ? this.detailColumns[0].key : "";
    },
    detailMetaColumns() {
      return this.detailColumns.slice(1);
    },
    singleAction() {
      return this.actions.length === 1 ? this.actions[0] : null;
    },
    sheetActions() {
      if (this.singleAction && this.singleAction.menu) {
        return this.singleAction.menu;
      }
      return this.actions.flatMap((action) => (action.menu ? action.menu : [action]));
    },
  },
  watch: {
    rows() {
      this.limit = this.pageSize;
    },
    aggregateRows() {
      this.limit = this.pageSize;
    },
    aggregate() {
      this.sort = { key: null, dir: 1 };
      this.limit = this.pageSize;
      this.expanded = null;
    },
  },
  methods: {
    isEmpty(value) {
      return value === undefined || value === null || value === "";
    },
    /** Polja kartice koja imaju vrednost (prazna se ne prikazuju). */
    visibleMeta(row) {
      return this.metaColumns.filter((column) => column.always || !this.isEmpty(row[column.key]));
    },
    show(value) {
      return value === undefined || value === null || value === "" ? "—" : value;
    },
    toneClass(tone) {
      return TONE_CLASS[tone] || "";
    },
    iconColor(tone) {
      if (tone === "ok" || tone === "pri") {
        return "#ffffff";
      }
      return tone === "danger" ? "#b42318" : undefined;
    },
    columnClass(column) {
      return { "is-right": column.align === "right", "is-center": column.align === "center" };
    },
    cellClass(column) {
      return {
        "is-right": column.align === "right",
        "is-center": column.align === "center",
        "is-name": column.main,
        "rd-num": column.align === "right" || column.num,
        "rd-muted": column.muted,
      };
    },
    rowClass(row) {
      return row.tone ? `wh-row--${row.tone}` : "";
    },
    sortBy(key) {
      this.sort = { key, dir: this.sort.key === key ? -this.sort.dir : 1 };
    },
    ariaSort(key) {
      if (this.sort.key !== key) {
        return null;
      }
      return this.sort.dir > 0 ? "ascending" : "descending";
    },
    sortArrow(key) {
      if (this.sort.key !== key) {
        return "";
      }
      return this.sort.dir > 0 ? "↑" : "↓";
    },
    toggleExpand(row) {
      this.expanded = this.expanded === row.key ? null : row.key;
    },
    openRow(row) {
      if (this.clickable) {
        this.$emit("open-row", row);
      }
    },
    onBarAction() {
      if (this.singleAction && !this.singleAction.menu) {
        this.$emit("action", this.singleAction.key);
        return;
      }
      this.actionsSheet = true;
    },
    runSheetAction(item) {
      this.actionsSheet = false;
      this.$emit("action", item.key);
    },
    /** Tabela kao tekst razdvojen tabulatorima (vidljivi redovi). */
    async copyTable() {
      const table = this.$refs.card && this.$refs.card.querySelector("table.rd-tbl");
      if (!table) {
        return;
      }
      const clean = (text) => text.replace(/\s+/g, " ").trim();
      const head = [...table.tHead.rows[0].cells].map((cell) => clean(cell.textContent));
      const keep = head.map((text, index) => (text ? index : -1)).filter((index) => index >= 0);
      const rows = [...table.tBodies]
        .flatMap((body) => [...body.rows])
        .filter((row) => row.cells.length === head.length)
        .map((row) => keep.map((index) => clean(row.cells[index].textContent)));
      const copied = await copyText(rowsToTsv(keep.map((index) => head[index]), rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
